
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Button, Icon } from '@express-labs/raven-ui';
import { cn } from '@express-labs/raven-tools';
import s from './PromoCode.scss';
import { addPromoCode, removePromoCode } from '../../../../../../graphql/mutation';
import { getOrderSummary } from '../../../../../../queries';
import { analyze } from '../../../../../../helpers';
import LongDescriptionModal from './LongDescriptionModal';


export const updateOrderSummaryQuery = (store, { data: { addPromoCode: addPromoCodeData } }) => {
  const orderData = store.readQuery({ query: getOrderSummary });
  /* istanbul ignore next */
  if (orderData) {
    store.writeQuery({
      query: getOrderSummary,
      data: {
        orderSummary: { ...orderData, ...addPromoCodeData },
      },
    });
  }
};
function PromoCode({
  code,
  expiryDate,
  longDescription,
  shortDescriptionCta,
  subheading,
}) {
  const [isError, setIsError] = useState(false);
  const [promoCodeResponse, setPromoCodeResponse] = useState(null);
  const [showCodeSelectionError, setCodeSelectionError] = useState(null);
  const [crmCodeVal, setCrmCodeVal] = useState(code);
  const [promoCodeError, setPromoCodeError] = useState('');
  const { data: orderSummary } = useQuery(getOrderSummary, {
    variables: {
      shouldRecalculateTax: false,
    },
  });
  const [onAddPromoCode] = useMutation(addPromoCode, {
    update: updateOrderSummaryQuery,
  });
  const isBagEmpty = orderSummary?.orderSummary?.lineItems?.length === 0;
  React.useEffect(() => {
    const promotions = orderSummary?.orderSummary
      ?.promotions.find((item) => item.code && item.walletCouponCode === code);
    setPromoCodeResponse(promotions);
  }, [code, orderSummary]);

  const errorHandler = (msg) => {
    setIsError(true);
    setPromoCodeError(msg);
    setPromoCodeResponse(null);
    setCodeSelectionError(true);
    /* istanbul ignore next */
    setTimeout(() => {
      setCodeSelectionError(false);
    }, 5000);
  };

  const handleSubmit = (e) => {
    const crmCode = e.target.parentNode.getAttribute('data-promoCode');

    /* istanbul ignore next */
    if (!crmCode) {
      errorHandler('There was an error applying this code to your cart.');
      return null; 
    }

    /* istanbul ignore next */
    if (isBagEmpty) {
      errorHandler('Add the items to your cart to redeem.');
      return null;
    }
    const isCoupenApplied = orderSummary?.orderSummary?.promotions
      ?.filter((item) => item?.code && item?.walletCouponCode)?.length > 0;
    if (isCoupenApplied) {
      errorHandler('You can only add one coupon per purchase.');
      return null;
    }
    analyze({
      name: 'MemberWalletAddCode',
    });

    setCrmCodeVal(crmCode);
    return onAddPromoCode({
      variables: {
        code: crmCode,
      },
    }).then((response) => {
      const hasErrors = !!response?.data?.addPromoCode?.errors?.length;
      if (hasErrors) {
        setIsError(hasErrors);
        if (response?.data?.addPromoCode?.lineItems?.length === 0) {
          errorHandler('Add the items to your cart to redeem.');
        } else {
          errorHandler('There was an error applying this code to your cart.');
        }
        setPromoCodeResponse(null);
      } else {
        setIsError(!!response.errors?.length);
        setPromoCodeResponse(response.data?.addPromoCode);
      }
      window?.Heartbeat?.beat();
    }).catch(() => {
      errorHandler('There was an error applying this code to your cart.');
    });
  };

  const [onRemovePromoCode] = useMutation(removePromoCode, {
    onCompleted(response) {
      setIsError(!response?.removePromoCode.errors?.length);
      setPromoCodeResponse(response?.removePromoCode.errors?.length);
    },
    onError() {
      setIsError(true);
      setPromoCodeError('There was an error removing your promo code');
      setPromoCodeResponse(null);
    },
    update(cache, { data: { removePromoCode: removePromoCodeResponse } }) {
      const orderData = cache.readQuery({ query: getOrderSummary });
      /* istanbul ignore next */
      if (removePromoCodeResponse) {
        cache.writeQuery({
          query: getOrderSummary,
          data: {
            orderSummary: { ...orderData, ...removePromoCodeResponse },
          },
        });
      }
    },
  });

  const deletePromoCode = (e) => {
    const crmCode = e.target.getAttribute('data-promocode');

    setCrmCodeVal(crmCode);
    return onRemovePromoCode({
      variables: {
        code: crmCode,
      },
    }).then(() => {
      window?.Heartbeat?.beat();
    });
  };

  const addCodeButton = (crmCode) => ((promoCodeResponse && crmCode === crmCodeVal) ? (
    <>
      <div className={s.successDiv}>
        <span className={cn(s.icon, s.iconCheck)}>
          <Icon icon="check" />
        </span>
        <span className={cn(s.successCode, 'bodyTertiary')}>
          {' '}
          CODE ADDED
        </span>
      </div>
      <Button
        as="button"
        theme="dark"
        size="small"
        className={s.removeButton}
        data-promocode={crmCode}
        onClick={deletePromoCode}
      >
        Remove
      </Button>
    </>
  ) : (
    <Button
      as="button"
      theme="dark"
      size="small"
      className={s.promoButton}
      data-promocode={crmCode}
      onClick={handleSubmit}>
      <span className={cn(s.icon, s.iconPlus)}>
        <Icon icon="plus" />
      </span>
      <span className={s.labelText}>Add code</span>
    </Button>
  )
  );

  const diffHours = (dt2, dt1) => {
    let diff = (dt2.getTime() - dt1.getTime()) / 1000;
    diff /= (60 * 60);
    return Math.abs(Math.round(diff));
  };
  const isWithIn24Hours = diffHours(new Date(expiryDate), new Date()) <= 24;
  const getExpireDate = (millis) => {
    const expireDate = new Date(millis);
    return `${expireDate.getMonth() + 1}/${expireDate.getDate()}/${expireDate.getFullYear()}`;
  };

  return (
    <div className={s.promoCodeCardContainer}>
      <div className={cn(s.promoCardInner)}>
        <div className={cn(s.promoCodeCardSubContainer, s.unSelectedPromo, s.promoCardFront)}>
          <div>
            <p className={s.promoDesc}>
              {subheading}
              {' '}
              <LongDescriptionModal
                shortDescriptionCta={shortDescriptionCta}
                longDescription={longDescription}
                subheading={subheading}
              />
            </p>
          </div>
          <div className={s.promoCardFooter}>
            {addCodeButton(code)}
            {!promoCodeResponse && crmCodeVal === code && <span className={cn(s.expireDate, isWithIn24Hours ? s.expiryDateError : '')}>{`Expires ${getExpireDate(expiryDate)}`}</span>}
          </div>
          <div className={cn(s.errorMessage, 'bodyTertiary')}>
            {isError && crmCodeVal === code && showCodeSelectionError && promoCodeError}
          </div>
        </div>
      </div>
    </div>
  );
}

PromoCode.propTypes = {
  code: PropTypes.string.isRequired,
  ctaOne: PropTypes.shape({
    href: PropTypes.string,
    title: PropTypes.string,
  }),
  ctaTwo: PropTypes.shape({
    href: PropTypes.string,
    title: PropTypes.string,
  }),
  longDescription: PropTypes.arrayOf(PropTypes.string),
  shortDescriptionCta: PropTypes.string,
  subheading: PropTypes.string,
  expiryDate: PropTypes.string,
};

PromoCode.defaultProps = {
  ctaOne: null,
  ctaTwo: null,
  longDescription: [''],
  shortDescriptionCta: 'See more',
  subheading: '',
  expiryDate: '',
};

export default PromoCode;
